import type { CSSProperties, ComponentProps, ReactNode } from 'react';
import { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';

type TooltipProps = Omit<ComponentProps<typeof FormattedMessage>, 'children'>;

const Wrapper = styled.div`
  overflow: auto;
  position: relative;
  isolation: isolate;
  font: var(--t-label-m);
`;

const Table = styled.table`
  width: 100%;
  color: var(--c-white);
  border-collapse: collapse;
  table-layout: fixed;

  --thead-row-height: var(--quadruple-unit);
  --tbody-row-height: calc(var(--quadruple-unit) + var(--intermediate-unit));
`;

const Td = styled.td`
  text-align: center;
  padding-inline: var(--intermediate-unit);
  height: var(--tbody-row-height);
`;

const Tr = styled.tr`
  padding-block: var(--unit);

  & > ${Td}:first-child {
    border-right: 1px solid var(--c-nd-50);
  }

  &:nth-child(even) {
    background-color: var(--c-nd-50);
  }
`;

const Thead = styled.thead`
  border-bottom: 1px solid var(--c-nd-50);

  td {
    height: var(--thead-row-height);
  }
`;

const CellTooltip = styled(Tooltip)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CellContent = ({ children, title }: CellProps): ReactNode => {
  const hasValidTooltip =
    typeof title === 'string' || (title?.id && title?.defaultMessage);

  if (!hasValidTooltip) {
    return children;
  }

  const tooltipTitle =
    typeof title === 'string' ? title : <FormattedMessage {...title} />;
  return <CellTooltip title={tooltipTitle}>{children}</CellTooltip>;
};

type CellProps = {
  className?: string;
  children: ReactNode;
  title?: string | TooltipProps;
  width?: CSSProperties['width'];
};

export const StatsTableCell = ({
  children,
  title = {},
  width,
  className,
}: CellProps) => {
  return (
    <Td className={className} style={{ width }}>
      <CellContent title={title}>{children}</CellContent>
    </Td>
  );
};

export const StatsTableEmptyCell = () => <StatsTableCell>–</StatsTableCell>;

type RowProps = {
  children: ReactNode;
};

export const StatsTableRow = ({ children }: RowProps) => {
  return <Tr>{children}</Tr>;
};

type HeaderProps = { children: ReactNode };

export const StatsTableHeader = ({ children }: HeaderProps) => {
  return (
    <Thead>
      <Tr>{children}</Tr>
    </Thead>
  );
};

type Props = {
  children: ReactNode;
};

export const StatsTable = ({ children }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Wrapper ref={containerRef}>
      <Table>{children}</Table>
    </Wrapper>
  );
};
